import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { User, Mail, Lock, Building2, ArrowRight, Zap, CheckCircle, X, Shield } from 'lucide-react';
import axios from 'axios';

const LOGO = 'https://customer-assets.emergentagent.com/job_cc5301f8-029c-4cb1-afaa-ab22368334bb/artifacts/yofpexsn_Logo%20-%20official%20300x300.png';
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const PLAN_FEATURES = {
  starter: { color: '#2E7D32', features: ['2 AI Employees', '100 messages/mo', 'Document upload', 'Basic reports'] },
  professional: { color: '#7B1FA2', features: ['6 AI Employees', '1,000 messages/mo', 'HRIS integrations', 'Advanced reports', 'Priority support'] },
  enterprise: { color: '#F9A825', features: ['All 11 AI Employees', 'Unlimited messages', 'SSO/OIDC', 'API access', 'Dedicated support'] },
};

export default function SignupModal({ onClose, initialPlan }) {
  const [step, setStep] = useState(1); // 1=info, 2=plan
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(initialPlan || 'professional');
  const [plans, setPlans] = useState({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND}/api/pricing`).then(r => { if (r.data) setPlans(r.data); }).catch(e => console.error('Pricing error:', e));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- runs once on mount

  const handleSubmit = async () => {
    setError(''); setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND}/api/auth/register-and-subscribe`, {
        name, email, password, plan: selectedPlan, company_name: companyName
      });
      // Save token (needed for post-checkout auth)
      localStorage.setItem('panzix_hr_token', res.data.token);  // eslint-disable-line -- token storage for auth redirect
      // Redirect to Stripe Checkout
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        window.location.href = '/';
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Registration failed. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'white', borderRadius: 24, width: '100%', maxWidth: step === 1 ? 480 : 800, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,0.3)' }}>
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #1B5E20, #4A148C)', padding: '24px 28px', borderRadius: '24px 24px 0 0', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src={LOGO} alt="" style={{ width: 36, height: 36, borderRadius: 8 }} />
            <div>
              <h3 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>Create Your Account</h3>
              <p style={{ fontSize: 12, opacity: 0.8, margin: 0 }}>Step {step} of 2 — {step === 1 ? 'Your Information' : 'Choose Your Plan'}</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white' }}><X size={16} /></button>
        </div>

        <div style={{ padding: '28px 32px' }}>
          {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

          {/* Step 1: Basic Info */}
          {step === 1 && (
            <>
              <div className="form-group">
                <label><User size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />Full Name</label>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="Jane Doe" data-testid="signup-name" />
              </div>
              <div className="form-group">
                <label><Mail size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />Work Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@company.com" data-testid="signup-email" />
              </div>
              <div className="form-group">
                <label><Lock size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />Password</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 8 characters" minLength={8} data-testid="signup-password" />
              </div>
              <div className="form-group">
                <label><Building2 size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />Company Name</label>
                <input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Acme Corp" data-testid="signup-company" />
              </div>
              <button className="btn-primary" onClick={() => {
                if (!name || !email || !password) { setError('Please fill in all fields'); return; }
                if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
                setError(''); setStep(2);
              }} style={{ width: '100%', padding: '14px', fontSize: 15, borderRadius: 12, marginTop: 8 }} data-testid="signup-next">
                Choose Plan <ArrowRight size={16} style={{ marginLeft: 8 }} />
              </button>
              <p style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', marginTop: 12 }}>Already have an account? <a href="/login" style={{ color: 'var(--panzix-green)', fontWeight: 600, textDecoration: 'none' }}>Sign In</a></p>
            </>
          )}

          {/* Step 2: Plan Selection */}
          {step === 2 && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16, background: '#f0fdf4', padding: '8px 14px', borderRadius: 10, fontSize: 13, color: '#166534', fontWeight: 600 }}>
                <Zap size={14} />14-day free trial on all plans — cancel anytime
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 20 }}>
                {Object.entries(plans).map(([id, plan]) => {
                  const features = PLAN_FEATURES[id] || PLAN_FEATURES.starter;
                  const isSelected = selectedPlan === id;
                  return (
                    <div key={id} onClick={() => setSelectedPlan(id)} style={{
                      border: `3px solid ${isSelected ? features.color : '#e2e8f0'}`, borderRadius: 16,
                      padding: 20, cursor: 'pointer', background: isSelected ? `${features.color}08` : 'white',
                      transition: 'all 0.2s', textAlign: 'center', position: 'relative',
                    }} data-testid={`signup-plan-${id}`}>
                      {id === 'professional' && <div style={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)', background: features.color, color: 'white', fontSize: 10, fontWeight: 700, padding: '2px 12px', borderRadius: 20 }}>POPULAR</div>}
                      <h4 style={{ color: features.color, fontSize: 16, fontWeight: 800, marginBottom: 4 }}>{plan.name}</h4>
                      <div style={{ fontSize: 32, fontWeight: 900, color: '#0f172a', margin: '6px 0' }}>${plan.price}<span style={{ fontSize: 13, fontWeight: 400, color: '#94a3b8' }}>/mo</span></div>
                      <p style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>{plan.agents} AI Employees</p>
                      <div style={{ textAlign: 'left' }}>
                        {features.features.map(f => <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151', padding: '3px 0' }}><CheckCircle size={12} color={features.color} />{f}</div>)}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn-secondary" onClick={() => setStep(1)} style={{ padding: '14px 24px', fontSize: 14, borderRadius: 12 }}>Back</button>
                <button className="btn-primary" onClick={handleSubmit} disabled={submitting} style={{ flex: 1, padding: '14px', fontSize: 15, borderRadius: 12 }} data-testid="signup-subscribe">
                  {submitting ? 'Setting up your account...' : <><Zap size={16} style={{ marginRight: 8 }} />Start Free Trial — {plans[selectedPlan]?.name || selectedPlan}</>}
                </button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 14 }}>
                {['256-bit SSL', 'Cancel Anytime', 'No charge for 14 days'].map(t => <span key={t} style={{ fontSize: 10, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}><Shield size={10} />{t}</span>)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
